import { ChangeDetectorRef, Component, ElementRef, forwardRef, inject, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { ItemsBarcodeQty } from '../item-general/item-general.model';
import { MatInputModule } from '@angular/material/input';
import { EditBarcodeDialogService } from 'src/app/dialogs/edit-barcode-dialog/edit-barcode-dialog.component';
import { ItemsBarcodes } from 'tilby-models';

@Component({
  selector: 'app-barcode-selector',
  standalone: true,
  imports: [
    MatChipsModule, 
    MatIconModule,
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule
  ],
  templateUrl: './barcode-selector.component.html',
  styleUrl: './barcode-selector.component.scss',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => BarcodeSelectorComponent),
    multi: true
  }],
})
export class BarcodeSelectorComponent implements ControlValueAccessor {

  private readonly editBarcodeDialogService = inject(EditBarcodeDialogService);

  internalControl = new FormControl(); 

  @ViewChild('chipsWrapper') wrapper!: ElementRef;
  @ViewChildren('chipEl', { read: ElementRef }) chipElements!: QueryList<ElementRef>;
  @ViewChild('extraBadgeMirror', { read: ElementRef }) extraBadgeMirrorEl!: ElementRef;

  barcodes: ItemsBarcodeQty[] = [];
  disabled = false;
  hiddenCount = 0;
  firstHiddenIndex = Infinity;
  firstChipMaxWidth: number | null = null;

  private resizeObserver: ResizeObserver;

  constructor(private cdr: ChangeDetectorRef) {
    this.resizeObserver = new ResizeObserver(() => this.calculateVisibleItems());
  }

  ngAfterViewInit() {
    if (this.wrapper && this.wrapper.nativeElement) {
      this.resizeObserver.observe(this.wrapper.nativeElement);
      this.calculateVisibleItems()
    }
  }

  ngOnDestroy() {
    this.resizeObserver.disconnect();
  }

  onChange = (val: any) => {};
  onTouched = () => {};

  writeValue(obj: any): void {
    // this.barcodes = obj?.map((b: any) => ({...b, qty: 2})) || [];
    this.barcodes = obj || [];
    this.internalControl.setValue(this.barcodes.length ? 'valid' : null);

    this.firstHiddenIndex = Infinity; 
    setTimeout(() => this.calculateVisibleItems(), 0);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  calculateVisibleItems() {
    if (!this.wrapper?.nativeElement) return;

    this.hiddenCount = 0;
    this.firstHiddenIndex = Infinity;
    this.firstChipMaxWidth = null;
    this.cdr.detectChanges();

    const chips = this.chipElements.toArray();
    if (chips.length === 0) return;

    const wrapperRect = this.wrapper.nativeElement.getBoundingClientRect();
    if (!wrapperRect) return;
    
    const wrapperTop = wrapperRect.top;
    const wrapperWidth = wrapperRect.width;
    const badgeWidth = this.extraBadgeMirrorEl.nativeElement.getBoundingClientRect().width + 8;

    let count = 0;
    let firstHidden = chips.length;

    for (let i = 0; i < chips.length; i++) {
      const chipTop = chips[i].nativeElement.getBoundingClientRect().top;

      // Se è il primo elemento non lo nascondiamo mai
      if(i === 0) continue;

      // Se l'elemento finisce su una nuova riga
      if (chipTop > wrapperTop + 10) {
        firstHidden = i;
        count = chips.length - i;
        break;
      }
    }

    if (count > 0) {
      // Spazio disponibile = Larghezza totale - larghezza badge - eventuali margini
      const maxAllowedForFirst = wrapperWidth - badgeWidth - 10;

      // Se c'è solo un elemento visibile (il primo) e il badge
      if (firstHidden === 1) {
        this.firstChipMaxWidth = maxAllowedForFirst;
      } else {
        // Se ci sono più elementi, controlliamo se l'ultimo visibile + badge escono
        const lastVisibleRect = chips[firstHidden - 1].nativeElement.getBoundingClientRect();
        if ((lastVisibleRect.right + badgeWidth) > wrapperRect.right) {
          // Se nascondere l'ultima chip visibile ci porta alla prima chip
          if (firstHidden === 2) {
              this.firstChipMaxWidth = maxAllowedForFirst;
              firstHidden = 1;
              count++;
          } else {
              firstHidden--;
              count++;
          }
        }
      }
    }

    this.firstHiddenIndex = firstHidden;
    this.hiddenCount = count;
    this.cdr.detectChanges();
  }

  async openModal(event: Event) {
    event.stopPropagation();
    const result = await this.editBarcodeDialogService.openDialog({
      data: {
        title: "Barcode",
        barcodes: this.barcodes
      }
    })

    if(result) {
      this.onModalClose(result);
      this.firstHiddenIndex = Infinity; 
      setTimeout(() => this.calculateVisibleItems(), 0);
    } 
  }

  onModalClose(newBarcodes: any[]) {
    this.barcodes = newBarcodes;
    this.internalControl.setValue(this.barcodes.length ? 'valid' : null);
    // this.onChange(this.barcodes);

    // TODO: da eliminare quando l'API gestità anche la quantità
    const dataToReturn: ItemsBarcodes[] = newBarcodes.map(item => {
      const { qty, ...cleanItem } = item; 
      Object.keys(cleanItem).forEach(key => 
        (cleanItem[key] === null) && delete cleanItem[key]
      )
      return cleanItem as ItemsBarcodes;
    });
    this.onChange(dataToReturn);
    // ----
    
    this.onTouched();
  }
}
